import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Eye, Fish, MapPin, ShieldAlert, Thermometer } from 'lucide-react';
import { ClassifyAlertDialog } from './classify-alert-dialog';
import { getAlertSensorType, hasVisualEvidence, type ClinicalCase } from '@/app/utils/vision-biology';

interface ClinicalCaseCardProps {
  item: ClinicalCase;
  apiUrl: string;
  authToken: string;
  publicAnonKey: string;
  onUpdate: () => void;
  autoOpen?: boolean;
  variant?: 'card' | 'row';
  selected?: boolean;
  onSelect?: () => void;
  selectable?: boolean;
  selectedForBulk?: boolean;
  onToggleBulkSelection?: () => void;
}

const statusTone: Record<ClinicalCase['validationStatus'], string> = {
  pending: 'border-amber-400/70 bg-amber-50/95 text-amber-700 shadow-sm backdrop-blur-sm',
  confirmed: 'border-emerald-400/70 bg-emerald-50/95 text-emerald-700 shadow-sm backdrop-blur-sm',
  rejected: 'border-slate-300/80 bg-slate-50/95 text-slate-700 shadow-sm backdrop-blur-sm',
  inconclusive: 'border-sky-400/70 bg-sky-50/95 text-sky-700 shadow-sm backdrop-blur-sm'
};

const statusLabel: Record<ClinicalCase['validationStatus'], string> = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  rejected: 'Rejeitado',
  inconclusive: 'Inconclusivo'
};

const severityTone: Record<ClinicalCase['severity'], string> = {
  critical: 'border-red-400/70 bg-red-50/95 text-red-700 shadow-sm backdrop-blur-sm',
  high: 'border-orange-400/70 bg-orange-50/95 text-orange-700 shadow-sm backdrop-blur-sm',
  medium: 'border-amber-400/70 bg-amber-50/95 text-amber-700 shadow-sm backdrop-blur-sm',
  low: 'border-emerald-400/70 bg-emerald-50/95 text-emerald-700 shadow-sm backdrop-blur-sm'
};

const severityLabel: Record<ClinicalCase['severity'], string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa'
};

const severityPill: Record<ClinicalCase['severity'], string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/18 dark:text-red-200',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-500/18 dark:text-orange-200',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-500/16 dark:text-amber-200',
  low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/16 dark:text-emerald-200',
};

const statusPill: Record<ClinicalCase['validationStatus'], string> = {
  confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/16 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/16 dark:text-amber-200',
  rejected: 'bg-slate-100 text-slate-700 dark:bg-slate-500/18 dark:text-slate-200',
  inconclusive: 'bg-sky-100 text-sky-700 dark:bg-sky-500/16 dark:text-sky-200',
};

const rowSeverityBadge: Record<ClinicalCase['severity'], string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/18 dark:text-red-200',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-500/18 dark:text-orange-200',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-500/16 dark:text-amber-200',
  low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/16 dark:text-emerald-200',
};

const rowStatusBadge: Record<ClinicalCase['validationStatus'], string> = {
  confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/16 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/16 dark:text-amber-200',
  rejected: 'bg-slate-100 text-slate-700 dark:bg-slate-500/18 dark:text-slate-200',
  inconclusive: 'bg-sky-100 text-sky-700 dark:bg-sky-500/16 dark:text-sky-200',
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isInternalId = (value: unknown) => {
  const text = String(value ?? '').trim();
  return UUID_PATTERN.test(text) || /^[0-9a-f]{12,}$/i.test(text);
};

const readableText = (value: unknown) => {
  const text = String(value ?? '').trim();
  return text && !isInternalId(text) ? text : null;
};

const normalizeSensorText = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const isTechnicalSensorAlias = (value: unknown, sensorType: string | null | undefined) => {
  const text = normalizeSensorText(value);
  const type = normalizeSensorText(sensorType);
  if (!text || !type) return false;
  const aliases = new Set([
    type,
    `sensor_${type}`,
    type === 'temperature' ? 'temp' : '',
    type === 'temperature' ? 'temperatura' : '',
    type === 'temperature' ? 'temperature' : '',
    type === 'temperature' ? 'sensor_de_temperatura' : '',
    type === 'dissolved_oxygen' ? 'oxygen' : '',
    type === 'dissolved_oxygen' ? 'oxigenio' : '',
    type === 'dissolved_oxygen' ? 'o2' : '',
    type === 'ph' ? 'sensor_de_ph' : '',
    type === 'redox' ? 'orp' : '',
    type === 'salinity' ? 'salinidade' : ''
  ].filter(Boolean));
  if (type === 'temperature' && /^(temp|temperatura|temperature)(_(temp|temperatura|temperature))*$/.test(text)) return true;
  if (type === 'dissolved_oxygen' && /^(o2|oxygen|oxigenio|oxigenio_dissolvido)(_(o2|oxygen|oxigenio|oxigenio_dissolvido))*$/.test(text)) return true;
  if (type === 'salinity' && /^(salinity|salinidade)(_(salinity|salinidade))*$/.test(text)) return true;
  return aliases.has(text);
};

const readableOperationalText = (value: unknown, sensorType: string | null | undefined) => {
  const text = readableText(value);
  return text && !isTechnicalSensorAlias(text, sensorType) ? text : null;
};

const sensorLabelFor = (sensorType: string | null | undefined) => {
  const type = String(sensorType ?? '').toLowerCase();
  if (type.includes('temperat')) return 'Temperatura';
  if (type.includes('ph')) return 'pH';
  if (type.includes('oxygen') || type.includes('oxig') || type.includes('o2')) return 'Oxigénio dissolvido';
  if (type.includes('redox') || type.includes('orp')) return 'Redox / ORP';
  if (type.includes('salin')) return 'Salinidade';
  return 'Sensor ambiental';
};

const formatCompactNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '');
  }
  return readableText(value);
};

const formatSensorUnit = (value: unknown) => {
  const unit = String(value ?? '').trim();
  if (!unit) return '';
  if (/^(c|celsius|ºc|°c)$/i.test(unit)) return ' °C';
  if (/^(mg_l|mg\/l)$/i.test(unit)) return ' mg/L';
  return ` ${unit}`;
};

const sensorAlertSummaryFor = (sensorType: string | null | undefined, metadata: Record<string, any>) => {
  const ruleText = [
    metadata.rule_message,
    metadata.message,
    metadata.description
  ].map((value) => readableOperationalText(value, sensorType)).find(Boolean);
  if (ruleText) return ruleText;
  const threshold = [
    metadata.threshold,
    metadata.threshold_description,
    metadata.rule_threshold,
    metadata.limit,
    metadata.limit_value,
    metadata.target_value
  ].map(formatCompactNumber).find(Boolean);
  if (threshold) {
    const label = sensorLabelFor(sensorType);
    const deviation = Number(metadata.deviation ?? metadata.delta);
    const condition = String(metadata.rule_condition ?? metadata.condition ?? '').toLowerCase();
    const unit = formatSensorUnit(metadata.unit);
    const isBelow = Number.isFinite(deviation)
      ? deviation < 0
      : /below|under|inferior|abaixo|menor|</.test(condition);
    return isBelow
      ? `${label} abaixo de ${threshold}${unit}`
      : `${label} excedeu ${threshold}${unit}`;
  }
  return 'Leitura ambiental a validar';
};

const sensorReadingLabelFor = (metadata: Record<string, any>, fallback: string) => {
  const value = [
    metadata.sensor_value,
    metadata.value,
    metadata.calibrated_value,
    metadata.raw_value,
    metadata.reading_value,
    metadata.last_value
  ].map(formatCompactNumber).find(Boolean);
  return value ? `${value}${formatSensorUnit(metadata.unit)}` : fallback;
};

const splitSensorReadingLabel = (value: string) => {
  const match = value.match(/^(.+?)(\s*(?:°C|ºC|mg\/L|mg\/l|ppm|ppt|%|pH))$/);
  return match ? { value: match[1].trim(), unit: match[2].trim() } : { value, unit: '' };
};

const extractSensorMeta = (item: ClinicalCase) => {
  const meta = item.rawAlert.metadata ?? {};
  const sensorType = getAlertSensorType(item.rawAlert);
  const origin = [
    meta.sensor_name,
    meta.sensor_label,
    meta.sensor_description,
    meta.sensor,
    meta.kit_name
  ].map((value) => readableOperationalText(value, sensorType)).find(Boolean) ?? sensorLabelFor(sensorType);
  const threshold = meta.threshold ?? meta.threshold_description ?? meta.rule_threshold ?? meta.limit ?? meta.limit_value;
  const deviation = meta.deviation ?? meta.delta;

  return { 
    origin, 
    threshold: formatCompactNumber(threshold), 
    deviation: formatCompactNumber(deviation) 
  };
};

const getAlertIconLabel = (item: ClinicalCase, sensorType = getAlertSensorType(item.rawAlert)): string => {
  const type = String(sensorType ?? item.alertType ?? item.rawAlert.alert_type ?? '').toLowerCase();
  if (type.includes('temperat')) return 'Temperatura';
  if (type.includes('ph')) return 'PH';
  if (type.includes('oxygen') || type.includes('oxig') || type.includes('o2')) return 'Oxigénio';
  if (type.includes('redox') || type.includes('orp')) return 'Redox';
  if (type.includes('salin')) return 'Salinidade';
  return 'Sensor';
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'A confirmar';
  return date.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const isGenericAlertTitle = (value: string | null | undefined) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return !normalized || ['sensor', 'a confirmar', 'detected', 'alerta', 'temp'].includes(normalized);
};

const titleCase = (value: string) =>
  value
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word ? word[0].toUpperCase() + word.slice(1) : word)
    .join(' ');

const buildAlertTitle = (item: ClinicalCase) => {
  const metadata = item.rawAlert.metadata ?? {};
  const sensorType = getAlertSensorType(item.rawAlert);
  if (sensorType) return sensorAlertSummaryFor(sensorType, metadata);
  const candidate = [
    item.alertType,
    item.suspectedPathology,
    item.rawAlert.problemName,
    item.rawAlert.aiAnalysis?.detectedIssues?.[0],
    metadata.anomaly_type,
    metadata.detection_status
  ].find((value) => !isGenericAlertTitle(value));
  const source = [
    metadata.camera_name,
    metadata.camera,
    metadata.sensor_name,
    metadata.sensor_label,
    metadata.sensor,
    metadata.kit_name
  ].map((value) => readableOperationalText(value, sensorType)).find(Boolean);
  const eventLabel = candidate ? titleCase(String(candidate)) : 'Anomalia detetada';
  return source ? `${source} - ${eventLabel}` : eventLabel;
};

type Bbox = { x: number; y: number; width: number; height: number; label?: string; source: 'yolo' | 'gemini' };

const normalizeBbox = (raw: any, source: Bbox['source'], imageSize?: { width: number; height: number } | null): Bbox | null => {
  if (!raw) return null;
  const arrayValue = Array.isArray(raw) ? raw : raw.bbox ?? raw.box ?? raw.boundingBox ?? raw.bounding_box ?? raw.bbox_2d;
  const values = Array.isArray(arrayValue)
    ? source === 'gemini' && arrayValue.length >= 4 && arrayValue.every((item) => Number.isFinite(Number(item))) && Math.max(...arrayValue.map(Number)) > 1
      ? {
        x: Number(arrayValue[1]),
        y: Number(arrayValue[0]),
        width: Number(arrayValue[3]) - Number(arrayValue[1]),
        height: Number(arrayValue[2]) - Number(arrayValue[0]),
        label: raw.label ?? raw.name
      }
      : { x: arrayValue[0], y: arrayValue[1], width: arrayValue[2], height: arrayValue[3], label: arrayValue[4] }
    : {
      x: raw.x ?? raw.left ?? raw.xmin ?? raw.x_min,
      y: raw.y ?? raw.top ?? raw.ymin ?? raw.y_min,
      width: raw.width ?? raw.w,
      height: raw.height ?? raw.h,
      label: raw.label
    };
  let x = Number(values.x);
  let y = Number(values.y);
  let width = Number(values.width ?? ((raw.xmax ?? raw.x_max ?? raw.right) != null ? Number(raw.xmax ?? raw.x_max ?? raw.right) - x : undefined));
  let height = Number(values.height ?? ((raw.ymax ?? raw.y_max ?? raw.bottom) != null ? Number(raw.ymax ?? raw.y_max ?? raw.bottom) - y : undefined));
  if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
  const maxValue = Math.max(x, y, width, height, x + width, y + height);
  if (source === 'gemini' && maxValue > 1 && maxValue <= 1000) {
    x /= 1000;
    width /= 1000;
    y /= 1000;
    height /= 1000;
  } else if ((x > 1 || y > 1 || width > 1 || height > 1) && imageSize?.width && imageSize?.height) {
    x /= imageSize.width;
    width /= imageSize.width;
    y /= imageSize.height;
    height /= imageSize.height;
  }
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
    width: Math.max(0.01, Math.min(1, width)),
    height: Math.max(0.01, Math.min(1, height)),
    label: values.label ? String(values.label) : undefined,
    source
  };
};

function AlertImage({ item, compact = false }: { item: ClinicalCase; compact?: boolean }) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [frameSize, setFrameSize] = useState<{ width: number; height: number } | null>(null);
  const metadata = item.rawAlert.metadata ?? {};
  const boxes = useMemo(() => {
    const yoloRaw = metadata.yolo_bbox ?? metadata.gemini_analysis?.yolo_bbox ?? metadata.detection?.bbox;
    const yolo = normalizeBbox(yoloRaw, 'yolo', imageSize);
    return [yolo].filter((box): box is Bbox => Boolean(box));
  }, [imageSize, metadata]);
  const displayBoxes = useMemo(() => {
    if (!imageSize?.width || !imageSize?.height || !frameSize?.width || !frameSize?.height) return boxes;
    const scale = Math.max(frameSize.width / imageSize.width, frameSize.height / imageSize.height);
    const renderedWidth = imageSize.width * scale;
    const renderedHeight = imageSize.height * scale;
    const offsetX = (frameSize.width - renderedWidth) / 2;
    const offsetY = (frameSize.height - renderedHeight) / 2;

    return boxes.map((box) => ({
      ...box,
      x: (offsetX + box.x * renderedWidth) / frameSize.width,
      y: (offsetY + box.y * renderedHeight) / frameSize.height,
      width: (box.width * renderedWidth) / frameSize.width,
      height: (box.height * renderedHeight) / frameSize.height
    }));
  }, [boxes, frameSize, imageSize]);

  useEffect(() => {
    const element = frameRef.current;
    if (!element) return;
    const updateFrameSize = () => setFrameSize({ width: element.clientWidth, height: element.clientHeight });
    updateFrameSize();
    const observer = new ResizeObserver(updateFrameSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (imageError || !item.image) {
    return (
      <div className="flex h-full items-center justify-center">
        <Eye className={compact ? 'h-8 w-8 text-muted-foreground' : 'h-10 w-10 text-muted-foreground'} />
      </div>
    );
  }

  return (
    <div ref={frameRef} className="relative h-full w-full overflow-hidden">
      <img
        src={item.image}
        alt={item.alertType ?? 'Alerta AQL Vision'}
        className="h-full w-full object-cover"
        onError={() => setImageError(true)}
        onLoad={(event) => setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
      />
      {displayBoxes.map((box, index) => (
        <div
          key={`${box.source}-${index}`}
          className={`pointer-events-none absolute border-2 shadow-[0_0_0_9999px_rgba(0,0,0,0.02)] ${
            box.source === 'gemini' ? 'border-fuchsia-300' : 'border-amber-300'
          }`}
          style={{
            left: `${box.x * 100}%`,
            top: `${box.y * 100}%`,
            width: `${box.width * 100}%`,
            height: `${box.height * 100}%`
          }}
          title={box.label}
        >
          <span className={`absolute -left-0.5 -top-5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-950 ${
            box.source === 'gemini' ? 'bg-fuchsia-300' : 'bg-amber-300'
          }`}>
            {box.source === 'gemini' ? 'LLM' : 'YOLO'}
          </span>
        </div>
      ))}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border bg-muted/25 p-2.5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-1 line-clamp-1 text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

export function ClinicalCaseCard({
  item,
  apiUrl,
  authToken,
  publicAnonKey,
  onUpdate,
  autoOpen = false,
  variant = 'card',
  selected,
  onSelect,
  selectable = false,
  selectedForBulk = false,
  onToggleBulkSelection
}: ClinicalCaseCardProps) {
  const [open, setOpen] = useState(autoOpen);
  const alertTitle = buildAlertTitle(item);
  const sensorMeta = extractSensorMeta(item);
  const sensorType = getAlertSensorType(item.rawAlert);
  const hasImageEvidence = Boolean(item.image) || hasVisualEvidence(item.rawAlert);
  const isSensor = Boolean(sensorType);
  const iconLabel = isSensor ? getAlertIconLabel(item, sensorType) : 'VISUAL';
  const sensorKindLabel = sensorLabelFor(sensorType);
  const evidenceLabel = hasImageEvidence ? 'EVIDÊNCIA VISUAL' : iconLabel;
  const metadata = item.rawAlert.metadata ?? {};
  const sensorReadingLabel = isSensor ? sensorReadingLabelFor(metadata, iconLabel) : iconLabel;
  const sensorReadingParts = splitSensorReadingLabel(sensorReadingLabel);
  const sensorOriginDetail = sensorMeta.origin && normalizeSensorText(sensorMeta.origin) !== normalizeSensorText(sensorKindLabel)
    ? sensorMeta.origin
    : null;
  const visualOrigin = [metadata.camera_name, metadata.camera, metadata.capture_source].map(readableText).find(Boolean) || sensorMeta.origin;
  const description = [
    isSensor ? sensorAlertSummaryFor(sensorType, metadata) : null,
    item.rawAlert.problemDescription,
    metadata.rule_message,
    metadata.message,
    metadata.description,
    item.notes,
    alertTitle
  ].map((value) => readableOperationalText(value, sensorType)).find(Boolean) ?? alertTitle;
  const detailRows: [string, string][] = [
    ...(!isSensor && description ? [['Descrição', description] as [string, string]] : []),
    ...(isSensor && sensorOriginDetail ? [['Origem', sensorOriginDetail] as [string, string]] : []),
    ...(!isSensor ? [['Origem', visualOrigin ?? 'Câmara / Modelo IA'] as [string, string]] : []),
    ...(isSensor && sensorMeta.threshold ? [['Limite', sensorMeta.threshold] as [string, string]] : []),
    ...(isSensor && sensorMeta.deviation ? [['Desvio', sensorMeta.deviation] as [string, string]] : []),
    ['Confiança', item.confidence !== null ? `${item.confidence}%` : 'A confirmar'],
    ['Registo', formatDateTime(item.capturedAt)],
  ];
  const isPending = item.validationStatus === 'pending';
  const actionLabel = isPending ? 'Validar' : 'Ver detalhes';
  const validationDate = formatDateTime(item.rawAlert.validated_at ?? item.rawAlert.validatedAt ?? item.capturedAt);
  const confidenceLabel = item.confidence !== null
    ? `${item.confidence}%`
    : isPending
      ? 'A confirmar'
      : 'Validado';
  const rowDateLabel = isPending ? formatDateTime(item.capturedAt) : `Validado em: ${validationDate}`;
  const actionClass = isPending
    ? 'bg-cyan-600 text-white hover:bg-cyan-700 disabled:bg-slate-300 disabled:text-slate-500 dark:bg-cyan-500/24 dark:text-cyan-50 dark:hover:bg-cyan-500/32'
    : 'bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-950/52 dark:text-slate-100 dark:hover:bg-slate-800/80';
  const rowAccentClass = item.severity === 'critical'
    ? 'bg-red-500'
    : item.severity === 'high'
      ? 'bg-orange-500'
      : item.severity === 'medium'
        ? 'bg-amber-500'
        : 'bg-emerald-500';
  const cardShellClass = `relative flex h-full flex-col overflow-hidden rounded-xl bg-card text-card-foreground shadow-sm transition-[box-shadow,transform,background-color] duration-200 group-hover:-translate-y-1 group-hover:scale-[1.01] group-hover:bg-muted/20 group-hover:shadow-xl group-focus-visible:ring-2 group-focus-visible:ring-cyan-500/30 motion-reduce:transform-none motion-reduce:transition-none dark:bg-slate-900 dark:group-hover:bg-slate-900/86`;
  const evidencePanelClass = 'relative shrink-0 overflow-hidden bg-muted/45 dark:bg-slate-950/45';
  const sensorEvidence = (
    <div className="flex h-full w-full flex-col items-center justify-center pt-2">
      <Thermometer className="h-10 w-10 text-primary/80 dark:text-cyan-300" strokeWidth={1.35} />
      <p className="mt-2 text-sm font-bold tracking-normal text-primary/80 dark:text-cyan-300">
        {sensorReadingLabel}
      </p>
    </div>
  );
  const cardSensorEvidence = (
    <div className="flex h-full w-full items-center justify-center px-5 pt-4">
      <div className="flex min-w-0 items-center justify-center gap-3">
        <Thermometer className="h-8 w-8 shrink-0 text-cyan-500 dark:text-cyan-200" strokeWidth={1.65} />
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="min-w-0 text-4xl font-bold leading-none tracking-normal text-slate-900 dark:text-slate-50">
            {sensorReadingParts.value}
          </span>
          {sensorReadingParts.unit ? (
            <span className="shrink-0 text-3xl font-bold leading-none tracking-normal text-slate-900 dark:text-slate-50">
              {sensorReadingParts.unit}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
  const imageEvidence = (
    <div className="h-full w-full">
      <AlertImage item={item} />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-card via-card/80 to-transparent" />
      {hasImageEvidence ? (
        <span className="absolute bottom-3 left-3 rounded-md border border-primary/25 bg-card/80 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary backdrop-blur">
          {evidenceLabel}
        </span>
      ) : null}
    </div>
  );

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  const bulkCheckbox = selectable ? (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggleBulkSelection?.();
      }}
      className={`absolute right-3 top-3 z-30 inline-flex h-7 w-7 items-center justify-center rounded-lg border text-xs font-black shadow-sm transition-colors ${
        selectedForBulk
          ? 'border-cyan-300 bg-cyan-500 text-white dark:text-slate-950'
          : 'border-border bg-card/90 text-muted-foreground hover:border-cyan-300 hover:text-cyan-700 dark:bg-slate-950/80 dark:hover:text-cyan-200'
      }`}
      aria-label={selectedForBulk ? 'Remover da seleção em lote' : 'Selecionar para validação em lote'}
      title={selectedForBulk ? 'Selecionado' : 'Selecionar'}
    >
      {selectedForBulk ? '✓' : ''}
    </button>
  ) : null;

  return (
    <>
      {variant === 'row' ? (
        <article data-demo-tour="clinical-card" className="group relative grid gap-4 overflow-hidden rounded-xl bg-card p-4 shadow-sm transition-[background-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:bg-muted/20 hover:shadow-lg focus-within:ring-2 focus-within:ring-cyan-500/30 motion-reduce:transform-none motion-reduce:transition-none dark:bg-slate-900 dark:hover:bg-slate-900/86 dark:hover:shadow-black/20 md:grid-cols-[112px_minmax(0,1fr)_auto] md:items-center">
          {bulkCheckbox}
          <span className={`absolute inset-y-3 left-0 w-1 rounded-r-full transition-[width,opacity] duration-200 ease-out group-hover:w-1.5 group-hover:opacity-100 motion-reduce:transition-none ${rowAccentClass}`} aria-hidden="true" />
          <div className={`${evidencePanelClass} h-24 rounded-lg bg-slate-100 transition-transform duration-200 ease-out group-hover:scale-[1.015] motion-reduce:transform-none motion-reduce:transition-none dark:bg-slate-950/45 md:h-[92px]`}>
            {hasImageEvidence ? imageEvidence : isSensor ? (
              <div className="h-full scale-75">{sensorEvidence}</div>
            ) : (
              <AlertImage item={item} compact />
            )}
          </div>

          <div className="min-w-0 space-y-2.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${rowSeverityBadge[item.severity]}`}>
                {severityLabel[item.severity]}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${rowStatusBadge[item.validationStatus]}`}>
                {statusLabel[item.validationStatus]}
              </span>
              <span className="inline-flex min-w-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                <Fish className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{[item.species, item.phase].filter(Boolean).join(' / ') || 'A confirmar'}</span>
              </span>
            </div>

            <div className="min-w-0">
              <h3 className="text-base font-bold leading-snug text-foreground md:text-lg">{alertTitle}</h3>
              <p className="mt-1 flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                {[item.site, item.tank].filter(Boolean).join(' / ') || 'Instalação a confirmar'}
              </p>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px]">
              {[
                ['Origem', isSensor ? sensorMeta.origin ?? 'Sensor' : visualOrigin ?? 'Câmara / Modelo IA'],
                ...(isSensor && sensorMeta.threshold ? [['Limite', sensorMeta.threshold] as [string, string]] : []),
                ...(isSensor && sensorMeta.deviation ? [['Desvio', sensorMeta.deviation] as [string, string]] : []),
                ['Confiança', confidenceLabel],
                ['Registo', rowDateLabel]
              ].map(([label, value]) => (
                <span key={label} className="inline-flex max-w-full items-baseline gap-1">
                  <span className="font-medium text-muted-foreground">{label}</span>
                  <span className="truncate font-semibold text-foreground">{value}</span>
                </span>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold shadow-sm transition-[background-color,transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md motion-reduce:transform-none motion-reduce:transition-none ${actionClass}`}
          >
            {isPending ? <ShieldAlert className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {actionLabel}
          </button>
        </article>
      ) : (
        <div
          role="button"
          tabIndex={0}
          data-demo-tour="clinical-card"
          onClick={() => {
            if (onSelect) onSelect();
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (onSelect) onSelect();
            setOpen(true);
          }}
          className="group w-full h-full text-left block"
        >
          <article className={cardShellClass}>
            {bulkCheckbox}
            <span className={`absolute inset-y-3 left-0 z-20 w-1 rounded-r-full transition-[width,opacity] duration-200 ease-out group-hover:w-1.5 motion-reduce:transition-none ${rowAccentClass}`} aria-hidden="true" />

            {/* Header: Imagem ou Icone */}
            <div className={`${evidencePanelClass} h-28 w-full`}>
              <span className={`absolute top-3 left-3 z-10 rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none ${severityPill[item.severity]}`}>
                {severityLabel[item.severity]}
              </span>
              <span className={`absolute top-3 right-3 z-10 rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none ${statusPill[item.validationStatus]}`}>
                {statusLabel[item.validationStatus]}
              </span>

              {hasImageEvidence ? imageEvidence : isSensor ? cardSensorEvidence : imageEvidence}
            </div>

            {/* Corpo */}
            <div className="flex flex-1 flex-col bg-card px-4 pb-4 pt-3">
              <p className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                <Fish className="h-3 w-3 shrink-0" />
                {[item.species, item.phase].filter(Boolean).join(' / ') || 'A confirmar'}
              </p>

              {isSensor ? null : (
                <h3 className="mt-1 text-base font-bold text-foreground leading-snug line-clamp-2">{alertTitle}</h3>
              )}

              <p className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <MapPin className="h-3 w-3 shrink-0" />
                {[item.site, item.tank].filter(Boolean).join(' / ') || 'A confirmar'}
              </p>

              <div className="mt-2.5 space-y-2 pt-1.5">
                {detailRows.map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-3 text-[11.5px]">
                    <span className="text-muted-foreground shrink-0">{label}</span>
                    <span className="font-semibold text-foreground text-right truncate">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      )}

      <ClassifyAlertDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        alert={item.rawAlert as any}
        apiUrl={apiUrl}
        authToken={authToken}
        publicAnonKey={publicAnonKey}
        onUpdate={() => {
          setOpen(false);
          onUpdate();
        }}
      />
    </>
  );
}
